import { useState } from 'react';
import { Modal, TextInput, NumberInput, Stack, Group, Button, Alert } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';

export interface AccessoryDistributor {
  id: number;
  accessoryId: number;
  name: string;
  link: string | null;
  contact: string | null;
  priceEur: number | null;
  addedAt: string;
  addedByUsername: string | null;
}

export default function AccessoryDistributorModal({
  accessoryId,
  initial,
  onClose,
  onDone,
}: {
  accessoryId: number;
  initial: AccessoryDistributor | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);

  const [name, setName] = useState(initial?.name ?? '');
  const [link, setLink] = useState(initial?.link ?? '');
  const [contact, setContact] = useState(initial?.contact ?? '');
  const [price, setPrice] = useState<number | string>(initial?.priceEur ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        link: link.trim() || null,
        contact: contact.trim() || null,
        priceEur: price === '' ? null : Number(price),
      };
      if (initial) await api.put(`/accessories/${accessoryId}/distributors/${initial.id}`, body);
      else await api.post(`/accessories/${accessoryId}/distributors`, body);
      notifications.show({ message: 'Distributor saved.', color: 'green' });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!initial) return;
    modals.openConfirmModal({
      title: `Delete ${initial.name}?`,
      children: 'This distributor will be removed.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/accessories/${accessoryId}/distributors/${initial.id}`);
          notifications.show({ message: 'Distributor deleted.', color: 'gray' });
          onDone();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  return (
    <Modal opened onClose={onClose} title={<span className="modal-title-custom">{initial ? 'Edit Distributor' : 'Add Distributor'}</span>}>
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required disabled={!writable} />
        <TextInput label="Link" value={link} onChange={(e) => setLink(e.currentTarget.value)} disabled={!writable} />
        <TextInput label="Contact" value={contact} onChange={(e) => setContact(e.currentTarget.value)} disabled={!writable} />
        <NumberInput label="Price (EUR)" value={price} onChange={setPrice} min={0} step={0.5} decimalScale={2} disabled={!writable} />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="space-between" mt="md">
          <div>
            {initial && writable && <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>}
          </div>
          <Group>
            <Button variant="default" onClick={onClose}>Close</Button>
            {writable && <Button onClick={save} loading={busy}>Save</Button>}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
